import { observer } from "mobx-react-lite";
import { UnlinkIcon } from "lucide-react";
import { KeyIcon } from "@heroicons/react/solid";

import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Variable } from "@/components/InputVariableConfigurator/types";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { usePlaygroundChatSettings } from "@/components/Playground/Drawer/Chat/hooks/usePlaygroundChatSettings";

interface VariableKeyInputProps {
  variable: Variable;
  onChange: (variable: Variable) => void;
  keyPlaceholder?: string;
  keyTooltip?: string;
  readOnly?: boolean;
  onTabPress?: () => void;
  onInputRef?: (ref: HTMLInputElement | null) => void;
}

const VariableKeyInput = observer(
  ({
    variable,
    onChange,
    keyPlaceholder,
    keyTooltip,
    readOnly = false,
    onInputRef,
    onTabPress,
  }: VariableKeyInputProps) => {
    const {
      isChatMode,
      isChatSettingsVariableKey,
      getChatSettingsKeyByVariableKey,
      getChatSettingsLabelByVariableKey,
      updateChatSettings,
    } = usePlaygroundChatSettings(variable.key);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Tab" && !e.shiftKey && onTabPress) {
        e.preventDefault();
        onTabPress();
      }
    };

    /**
     * Extension to support chat settings variables
     * in Playground in chat mode
     */
    const isChatSettingsVariable = isChatMode && isChatSettingsVariableKey;
    const chatSettingsVariableLabel = getChatSettingsLabelByVariableKey();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...variable, key: e.target.value });

      const chatSettingsKey = getChatSettingsKeyByVariableKey();
      if (chatSettingsKey) {
        updateChatSettings(chatSettingsKey, e.target.value);
      }
    };

    return (
      <div className="flex-1">
        <div className="relative">
          <BasicTooltip
            content={
              keyTooltip ||
              `Variable name ${isChatSettingsVariable ? `-  ${chatSettingsVariableLabel} in chat settings` : ""}`
            }
          >
            <KeyIcon
              className={cn("absolute left-3 top-2.5 h-4 w-4 text-gray-400", {
                "text-blue-500": isChatSettingsVariable,
              })}
            />
          </BasicTooltip>
          <Input
            ref={onInputRef}
            readOnly={readOnly}
            placeholder={keyPlaceholder || "Variable name"}
            className={cn("min-w-[150px] pl-9 pr-10 font-mono", {
              "pr-10": isChatSettingsVariable,
            })}
            value={variable.key}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
          />
          {isChatSettingsVariable && (
            <BasicTooltip content="Unlink variable from chat settings">
              <Button
                variant="ghost"
                size="tinyIcon"
                onClick={() => updateChatSettings(getChatSettingsKeyByVariableKey(), null)}
                className="absolute right-2 top-1"
              >
                <UnlinkIcon className="h-4 w-4 text-blue-500" />
              </Button>
            </BasicTooltip>
          )}
        </div>
      </div>
    );
  },
);

export default VariableKeyInput;
